import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { attendanceAPI } from '../services/api';
import {
  FaHome,
  FaCalendarCheck,
  FaUmbrellaBeach,
  FaCalendarAlt,
  FaChartLine,
  FaFileAlt,
  FaUsersCog,
  FaUser,
  FaSignOutAlt,
  FaChevronDown,
  FaFolder,
  FaHouseUser,
  FaClock,
  FaUserShield,
  FaUsers
} from 'react-icons/fa';

const Navigation = () => {
  const { user, logout, isAdmin, isHR } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [workingTime, setWorkingTime] = useState('00:00:00');

  useEffect(() => {
    fetchTodayAttendance();

    // Listen for attendance updates from other components
    const handleAttendanceUpdate = () => {
      fetchTodayAttendance();
    };

    window.addEventListener('attendanceUpdated', handleAttendanceUpdate);

    return () => {
      window.removeEventListener('attendanceUpdated', handleAttendanceUpdate);
    };
  }, []);

  useEffect(() => {
    let intervalId;

    if (todayAttendance?.punch_in && !todayAttendance?.punch_out) {
      // Start timer if punched in but not punched out
      intervalId = setInterval(() => {
        const punchInTime = new Date(todayAttendance.punch_in);
        const currentTime = new Date();
        const diffMs = currentTime - punchInTime;
        setWorkingTime(formatDuration(diffMs));
      }, 1000);
    } else if (todayAttendance?.punch_in && todayAttendance?.punch_out) {
      // Show total time if punched out
      const punchInTime = new Date(todayAttendance.punch_in);
      const punchOutTime = new Date(todayAttendance.punch_out);
      const diffMs = punchOutTime - punchInTime;
      setWorkingTime(formatDuration(diffMs));
    } else {
      setWorkingTime('00:00:00');
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [todayAttendance]);

  const fetchTodayAttendance = async () => {
    try {
      const response = await attendanceAPI.getToday();
      setTodayAttendance(response.data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const formatDuration = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isActiveGroup = (paths) => {
    return paths.some(path => location.pathname === path);
  };

  const navLinkStyle = (path) => ({
    color: isActive(path) ? '#635bff' : '#6b7280',
    fontSize: '14px',
    fontWeight: isActive(path) ? '600' : '500',
    padding: '6px 12px',
    marginLeft: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    backgroundColor: isActive(path) ? '#f0f0ff' : 'transparent',
  });

  const dropdownItemStyle = {
    fontSize: '14px',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#374151',
    textDecoration: 'none',
    transition: 'all 0.2s ease'
  };

  return (
    <>
      <Navbar expand="lg" style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '8px 0',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
      }}>
        <Container>
          <Navbar.Brand
            as={Link}
            to="/"
            style={{
              color: '#0a0a0a',
              fontWeight: '700',
              fontSize: '20px',
              letterSpacing: '-0.02em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <img
              src="https://www.wishfin.com/images/home-page/wishfin-logo.svg"
              alt="Wishfin Logo"
              style={{ height: '28px' }}
            />
            HRMS
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto" style={{ marginLeft: '24px', display: 'flex', alignItems: 'center' }}>
              <Nav.Link
                as={Link}
                to="/"
                style={navLinkStyle('/')}
              >
                <FaHome size={14} />
                Feed
              </Nav.Link>

              {/* Attendance Dropdown */}
              <NavDropdown
                title={
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: isActiveGroup(['/attendance', '/employee-attendance']) ? '#635bff' : '#6b7280',
                    fontSize: '14px',
                    fontWeight: isActiveGroup(['/attendance', '/employee-attendance']) ? '600' : '500'
                  }}>
                    <FaCalendarCheck size={14} />
                    Attendance
                  </span>
                }
                id="attendance-dropdown"
                style={{
                  marginLeft: '4px',
                  backgroundColor: isActiveGroup(['/attendance', '/employee-attendance']) ? '#f0f0ff' : 'transparent',
                  borderRadius: '6px',
                  padding: '0'
                }}
              >
                <NavDropdown.Item
                  as={Link}
                  to="/attendance"
                  style={dropdownItemStyle}
                >
                  <FaUser size={13} />
                  My Attendance
                </NavDropdown.Item>
                {(isAdmin || isHR) && (
                  <NavDropdown.Item
                    as={Link}
                    to="/employee-attendance"
                    style={dropdownItemStyle}
                  >
                    <FaUsers size={13} />
                    Employee Attendance
                  </NavDropdown.Item>
                )}
              </NavDropdown>

              {/* Leaves & WFH Dropdown */}
              <NavDropdown
                title={
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: isActiveGroup(['/leaves', '/wfh']) ? '#635bff' : '#6b7280',
                    fontSize: '14px',
                    fontWeight: isActiveGroup(['/leaves', '/wfh']) ? '600' : '500'
                  }}>
                    <FaUmbrellaBeach size={14} />
                    Leaves & WFH
                  </span>
                }
                id="leaves-wfh-dropdown"
                style={{
                  marginLeft: '4px',
                  backgroundColor: isActiveGroup(['/leaves', '/wfh']) ? '#f0f0ff' : 'transparent',
                  borderRadius: '6px',
                  padding: '0'
                }}
              >
                <NavDropdown.Item
                  as={Link}
                  to="/leaves"
                  style={dropdownItemStyle}
                >
                  <FaUmbrellaBeach size={13} />
                  Leaves
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={Link}
                  to="/wfh"
                  style={dropdownItemStyle}
                >
                  <FaHouseUser size={13} />
                  Work From Home
                </NavDropdown.Item>
              </NavDropdown>

              <Nav.Link
                as={Link}
                to="/holidays"
                style={navLinkStyle('/holidays')}
              >
                <FaCalendarAlt size={14} />
                Holidays
              </Nav.Link>

              {/* Files & Reports Dropdown */}
              <NavDropdown
                title={
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: isActiveGroup(['/files', '/reports', '/bcp-matrix']) ? '#635bff' : '#6b7280',
                    fontSize: '14px',
                    fontWeight: isActiveGroup(['/files', '/reports', '/bcp-matrix']) ? '600' : '500'
                  }}>
                    <FaFolder size={14} />
                    Resources
                  </span>
                }
                id="resources-dropdown"
                style={{
                  marginLeft: '4px',
                  backgroundColor: isActiveGroup(['/files', '/reports', '/bcp-matrix']) ? '#f0f0ff' : 'transparent',
                  borderRadius: '6px',
                  padding: '0'
                }}
              >
                <NavDropdown.Item
                  as={Link}
                  to="/files"
                  style={dropdownItemStyle}
                >
                  <FaFolder size={13} />
                  Files
                </NavDropdown.Item>
                {(isAdmin || isHR) && (
                  <NavDropdown.Item
                    as={Link}
                    to="/reports"
                    style={dropdownItemStyle}
                  >
                    <FaFileAlt size={13} />
                    Reports
                  </NavDropdown.Item>
                )}
                <NavDropdown.Item
                  as={Link}
                  to="/bcp-matrix"
                  style={dropdownItemStyle}
                >
                  <FaUserShield size={13} />
                  BCP Matrix
                </NavDropdown.Item>
              </NavDropdown>

              {(isAdmin || isHR) && (
                <Nav.Link
                  as={Link}
                  to="/employees"
                  style={navLinkStyle('/employees')}
                >
                  <FaUsersCog size={14} />
                  Employees
                </Nav.Link>
              )}
            </Nav>
            <Nav style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Working Hours Timer */}
              {todayAttendance?.punch_in && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: todayAttendance?.punch_out ? '#f3f4f6' : '#e8e5ff',
                  borderRadius: '8px',
                  border: `1px solid ${todayAttendance?.punch_out ? '#e5e7eb' : '#635bff'}`,
                  marginRight: '8px'
                }}>
                  <FaClock
                    size={14}
                    style={{
                      color: todayAttendance?.punch_out ? '#6b7280' : '#635bff'
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      lineHeight: '1'
                    }}>
                      {todayAttendance?.punch_out ? 'Total Time' : 'Working'}
                    </span>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: todayAttendance?.punch_out ? '#374151' : '#635bff',
                      fontFamily: 'monospace',
                      lineHeight: '1.2',
                      marginTop: '2px'
                    }}>
                      {workingTime}
                    </span>
                  </div>
                </div>
              )}
              <NavDropdown
                title={
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    transition: 'background-color 0.2s ease'
                  }}>
                    <span style={{
                      backgroundColor: '#635bff',
                      color: 'white',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      fontSize: '13px',
                      flexShrink: 0
                    }}>
                      {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                    <span style={{
                      color: '#374151',
                      fontWeight: '500',
                      fontSize: '14px',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {user?.full_name || 'User'}
                    </span>
                    <FaChevronDown size={10} style={{ color: '#9ca3af' }} />
                  </div>
                }
                id="basic-nav-dropdown"
                align="end"
                style={{
                  marginLeft: '8px'
                }}
              >
                <NavDropdown.Item
                  as={Link}
                  to="/profile"
                  style={{
                    fontSize: '14px',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <FaUser size={13} />
                  Profile
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item
                  onClick={handleLogout}
                  style={{
                    fontSize: '14px',
                    padding: '10px 16px',
                    color: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <FaSignOutAlt size={13} />
                  Logout
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <Container
        className="mt-4 mb-5"
        style={{
          backgroundColor: '#f9fafb',
          maxWidth: '1400px'
        }}
      >
        <Outlet />
      </Container>
    </>
  );
};

export default Navigation;
